import DOMPurify from "dompurify";
import { isPlainOnlyField } from "./richTextField";

const BLOCK_ALLOWED_TAGS = [
  "p",
  "br",
  "strong",
  "em",
  "b",
  "i",
  "u",
  "a",
  "ul",
  "ol",
  "li",
  "h1",
  "h2",
  "h3",
  "blockquote",
  "code",
  "pre",
];

const INLINE_ALLOWED_TAGS = ["strong", "em", "b", "i", "u", "a", "br", "span"];

const ALLOWED_ATTR = ["href", "target", "rel"];

const BLOCK_HOST_TAGS = new Set([
  "p",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "span",
  "li",
  "a",
]);

function escapeHtml(text) {
  return String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Decode entities when the CMS stored escaped markup like &lt;p&gt;…&lt;/p&gt;. */
export function decodeHtmlEntities(text) {
  const value = String(text ?? "");
  if (!value || !/&(?:lt|gt|amp|quot|#0?39|#x0?27);/i.test(value)) {
    return value;
  }

  if (typeof document !== "undefined") {
    const el = document.createElement("textarea");
    el.innerHTML = value;
    return el.value;
  }

  return value
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#0?39;/gi, "'")
    .replace(/&#x0?27;/gi, "'")
    .replace(/&amp;/gi, "&");
}

export function sanitizeRichHtml(html, { inline = false } = {}) {
  return DOMPurify.sanitize(html || "", {
    ALLOWED_TAGS: inline ? INLINE_ALLOWED_TAGS : BLOCK_ALLOWED_TAGS,
    ALLOWED_ATTR,
  });
}

export function isHtmlContent(text) {
  return /<[a-z][\s\S]*>/i.test(String(text || ""));
}

function looksLikeEscapedHtml(text) {
  return /&lt;[a-z][\s\S]*&gt;/i.test(String(text || ""));
}

function blockHtmlFromPlain(text) {
  if (/\n\s*\n/.test(text)) {
    const paragraphs = text
      .split(/\n\s*\n/)
      .map((paragraph) => paragraph.trim())
      .filter(Boolean);
    return paragraphs
      .map(
        (paragraph) =>
          `<p>${escapeHtml(paragraph).replace(/\n/g, "<br>")}</p>`
      )
      .join("");
  }

  if (/\n/.test(text)) {
    return `<p>${escapeHtml(text).replace(/\n/g, "<br>")}</p>`;
  }

  return `<p>${escapeHtml(text)}</p>`;
}

function inlineHtmlFromPlain(text) {
  return escapeHtml(text).replace(/\n/g, "<br>");
}

export function unwrapSingleParagraph(html) {
  const trimmed = String(html || "").trim();
  const match = trimmed.match(/^<p>([\s\S]*)<\/p>$/i);
  if (!match || match[1].includes("<p>")) {
    return trimmed;
  }
  return match[1].trim();
}

function blockToInlineHtml(html) {
  return String(html || "")
    .replace(/<\/?(p|div|h[1-6])>/gi, "")
    .replace(/<br\s*\/?>/gi, "<br>")
    .trim();
}

export function hasMultipleBlocks(html) {
  return /<\/p>\s*<p>/i.test(String(html || ""));
}

export function toRichHtml(content, { inline = false } = {}) {
  if (content == null || content === "") return "";

  let text = String(content).trim();
  if (!text) return "";

  // If the editor/API double-escaped HTML, restore real tags before sanitizing.
  if (!isHtmlContent(text) && looksLikeEscapedHtml(text)) {
    text = decodeHtmlEntities(text).trim();
  }

  if (isHtmlContent(text)) {
    const sanitized = sanitizeRichHtml(text, { inline: false });
    if (inline) {
      return sanitizeRichHtml(blockToInlineHtml(sanitized), { inline: true });
    }
    return sanitized;
  }

  const html = inline ? inlineHtmlFromPlain(text) : blockHtmlFromPlain(text);
  return sanitizeRichHtml(html, { inline });
}

export function toInlineRichHtml(content) {
  return toRichHtml(content, { inline: true });
}

export function prepareRichHtmlForHost(html, hostTag, { inline = false } = {}) {
  const safeHtml = inline ? toInlineRichHtml(html) : toRichHtml(html);
  if (!safeHtml) return { html: "", tag: hostTag };

  if (inline || BLOCK_HOST_TAGS.has(hostTag)) {
    return {
      html: inline ? safeHtml : unwrapSingleParagraph(safeHtml),
      tag: hostTag,
    };
  }

  if (hostTag === "div" && !hasMultipleBlocks(safeHtml)) {
    return {
      html: unwrapSingleParagraph(safeHtml),
      tag: "p",
    };
  }

  return { html: safeHtml, tag: hostTag };
}

export function normalizeEditorHtml(html) {
  const trimmed = String(html || "").trim();
  if (!trimmed || trimmed === "<p></p>" || trimmed === "<p><br></p>") {
    return "";
  }
  return trimmed;
}

export function sanitizeStoredFieldValue(value, field = {}) {
  const raw = value ?? "";
  if (raw === "") return "";

  if (field?.isRichText === false) {
    return String(raw);
  }

  if (isPlainOnlyField(field)) {
    return String(raw);
  }

  const type = String(field?.fieldType || "text").toLowerCase();
  if (!["text", "textarea", "rich-text"].includes(type)) {
    return raw;
  }

  let text = String(raw);
  if (!isHtmlContent(text) && looksLikeEscapedHtml(text)) {
    text = decodeHtmlEntities(text);
  }

  if (!isHtmlContent(text)) {
    return String(raw);
  }

  return sanitizeRichHtml(normalizeEditorHtml(text), { inline: false });
}

export function stripRichText(content) {
  if (content == null || content === "") return "";
  if (typeof document === "undefined") {
    return String(content).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  }
  const el = document.createElement("div");
  el.innerHTML = sanitizeRichHtml(toRichHtml(content, { inline: false }), {
    inline: false,
  });
  return (el.textContent || el.innerText || "").replace(/\s+/g, " ").trim();
}
